import React, { useState, useEffect } from 'react';
import {
  Container, Card, Row, Col, Form, Button, Alert, Badge
} from 'react-bootstrap';
import {
  FaArrowLeft, FaCog, FaSave, FaPlus, FaTrash, FaStar, FaTimes
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import AppToast from '../AppToast';
import './css/FieldManager.css';

const DependentValueManager = ({
  objectName,
  objectId,
  onBack,
  fields,
  dependencies,
  onNavigate,
  onGoBack,
  currentPageTitle
}) => {
  // Set default values for optional props
  const handleGoBack = onGoBack || onBack;
  const pageTitle = currentPageTitle || 'Configure Dependent Values';

  // Reusable header component for consistent layout
  const renderHeader = (title = pageTitle, subtitle = `Object: ${objectName}`) => (
    <div className="mb-4">
      {/* Top Back Button - Always Visible */}
      <div className="mb-3">
        <Button
          variant="outline-secondary"
          onClick={handleGoBack}
        >
          <FaArrowLeft className="me-1" />
          Back
        </Button>
      </div>

      {/* Main Header Content */}
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h2 className="mb-1">{title}</h2>
          <p className="text-muted mb-0">{subtitle}</p>
        </div>
      </div>
    </div>
  );
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [valueConfigs, setValueConfigs] = useState([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [controllingValues, setControllingValues] = useState([]);
  const [dependentValues, setDependentValues] = useState([]);

  // Toast helper functions
  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  // Filter picklist fields from the passed fields
  const picklistFields = fields && fields.length > 0 ? fields.filter(field =>
    field.data_type === 'picklist' || field.data_type === 'multipicklist'
  ) : [];

  console.log('DependentValueManager - All fields:', fields);
  console.log('DependentValueManager - Picklist fields:', picklistFields);

  // Get the single dependency (since we're now passing only one)
  const dependency = dependencies && dependencies.length > 0 ? dependencies[0] : null;

  // Get controlling field info
  const controllingField = dependency ? picklistFields.find(f => f.id === dependency.controlling_field) : null;
  const dependentField = dependency ? picklistFields.find(f => f.id === dependency.dependent_field) : null;

  console.log('DependentValueManager - Dependencies:', dependencies);
  console.log('DependentValueManager - Dependency:', dependency);
  console.log('DependentValueManager - Controlling field found:', controllingField);
  console.log('DependentValueManager - Dependent field found:', dependentField);

  // Get actual picklist values from the fields
  const getControllingFieldValues = async (fieldId) => {
    try {
      console.log('Fetching controlling field values for field ID:', fieldId);
      const dataApi = new DataApi('picklist/field');
      const response = await dataApi.fetchById(fieldId);

      console.log('Controlling field values response:', response);

      if (response.data.success) {
        const picklistValues = response.data.data || [];
        console.log('Controlling field picklist values:', picklistValues);
        return picklistValues.map(val => ({
          id: val.id,
          value_key: val.value_key,
          value_label: val.value_label,
          field_id: fieldId
        }));
      } else {
        console.error('Failed to fetch controlling field values:', response.data.message);
      }
      return [];
    } catch (error) {
      console.error('Error fetching controlling field values:', error);
      return [];
    }
  };

  const getDependentFieldValues = async (fieldId) => {
    try {
      console.log('Fetching dependent field values for field ID:', fieldId);
      const dataApi = new DataApi('picklist/field');
      const response = await dataApi.fetchById(fieldId);

      console.log('Dependent field values response:', response);

      if (response.data.success) {
        const picklistValues = response.data.data || [];
        console.log('Dependent field picklist values:', picklistValues);
        return picklistValues.map(val => ({
          id: val.id,
          value_key: val.value_key,
          value_label: val.value_label,
          field_id: fieldId
        }));
      } else {
        console.error('Failed to fetch dependent field values:', response.data.message);
      }
      return [];
    } catch (error) {
      console.error('Error fetching dependent field values:', error);
      return [];
    }
  };

  useEffect(() => {
    // Initialize value configuration for the single dependency
    if (dependency && controllingField && dependentField) {
      console.log('DependentValueManager - Initializing with dependency:', dependency);
      console.log('DependentValueManager - Controlling field:', controllingField);
      console.log('DependentValueManager - Dependent field:', dependentField);
      console.log('DependentValueManager - Controlling field ID:', dependency.controlling_field);
      console.log('DependentValueManager - Dependent field ID:', dependency.dependent_field);

      const initializeConfig = async () => {
        setLoading(true); // Set loading to true when initializing
        try {
          const controllingValuesData = await getControllingFieldValues(dependency.controlling_field);
          const dependentValuesData = await getDependentFieldValues(dependency.dependent_field);

          setControllingValues(controllingValuesData);
          setDependentValues(dependentValuesData);

          console.log('DependentValueManager - Controlling values:', controllingValuesData);
          console.log('DependentValueManager - Dependent values:', dependentValuesData);

          const config = {
            dependency_id: dependency.id,
            controlling_field: dependency.controlling_field,
            controlling_field_name: controllingField.field_name,
            controlling_field_label: controllingField.field_label,
            dependent_field: dependency.dependent_field,
            dependent_field_name: dependentField.field_name,
            dependent_field_label: dependentField.field_label,
            values: controllingValuesData.map(v => ({
              ...v,
              selected_dependent_values: [] // Will be populated with existing data
            })),
            dependent_values: dependentValuesData // Store dependent values for easy access
          };

          console.log('DependentValueManager - Config created:', config);
          setValueConfigs([config]);
        } catch (error) {
          console.error('Error initializing config:', error);
        } finally {
          setLoading(false); // Set loading to false after initialization
          setIsInitializing(false); // Set initializing to false
        }
      };

      initializeConfig();
    }
  }, [dependency, controllingField, dependentField]);

  // Fetch and populate existing value dependencies after config is created
  useEffect(() => {
    if (valueConfigs.length > 0 && dependency) {
      const populateExistingData = async () => {
        try {
          const existingDependencies = await fetchExistingValueDependencies(dependency.id);
          console.log('Populating with existing dependencies:', existingDependencies);

          if (existingDependencies.length > 0) {
            setValueConfigs(prev => prev.map(config => ({
              ...config,
              values: config.values.map(v => {
                // Find existing dependencies for this controlling value
                const existingDeps = existingDependencies.filter(ed =>
                  ed.controlling_picklist_values_id === v.id
                );

                // Get the dependent value IDs that match the existing dependencies
                const dependentValues = getDependentValuesForField(config.dependent_field);
                const selectedDependentValueIds = existingDeps.map(ed => {
                  return ed.dependent_picklist_values_id;
                }).filter(id => id !== null);

                return {
                  ...v,
                  selected_dependent_values: selectedDependentValueIds
                };
              })
            })));
          }
        } catch (error) {
          console.error('Error populating existing data:', error);
        }
      };

      populateExistingData();
    }
  }, [valueConfigs.length, dependency]);

  // Fetch existing value dependencies from database
  const fetchExistingValueDependencies = async (fieldDependencyId) => {
    try {
      const dataApi = new DataApi('picklist/value-dependencies');
      const response = await dataApi.fetchById(fieldDependencyId);

      if (response.data.success) {
        const existingDependencies = response.data.data || [];
        console.log('Fetched existing value dependencies:', existingDependencies);
        return existingDependencies;
      }
      return [];
    } catch (error) {
      console.error('Error fetching existing value dependencies:', error);
      return [];
    }
  };

  const handleDependentValueChange = (dependencyId, controllingValueId, selectedValues) => {
    setValueConfigs(prev => prev.map(config => {
      if (config.dependency_id === dependencyId) {
        return {
          ...config,
          values: config.values.map(v => {
            if (v.id === controllingValueId) {
              return {
                ...v,
                selected_dependent_values: selectedValues
              };
            }
            return v;
          })
        };
      }
      return config;
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Format data for backend: { controlling_picklist_value_id: [dependent_picklist_value_ids] }
      const valueConfigsFormatted = {};

      valueConfigs.forEach(config => {
        config.values.forEach(value => {
          if (value.selected_dependent_values.length > 0) {
            valueConfigsFormatted[value.id] = value.selected_dependent_values;
          }
        });
      });

      console.log('Formatted value configurations:', valueConfigsFormatted);

      // Call the value-dependencies endpoint
      const dataApi = new DataApi('picklist/value-dependencies');
      const response = await dataApi.create({
        field_dependency_id: dependencies[0].id,
        value_configs: valueConfigsFormatted
      });

      if (response.data.success) {
        showToastMessage('Value configurations saved successfully!', 'success');
      } else {
        showToastMessage(response.data.message || 'Failed to save value configurations', 'danger');
      }
    } catch (error) {
      console.error('Error saving value configurations:', error);
      showToastMessage('Failed to save value configurations', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const getDependentFieldForDependency = (dependencyId) => {
    const config = valueConfigs.find(c => c.dependency_id === dependencyId);
    if (config) {
      return picklistFields.find(f => f.id === config.dependent_field);
    }
    return null;
  };

  const getDependentValuesForField = (fieldId) => {
    const config = valueConfigs.find(c => c.dependent_field === fieldId);
    if (config && config.dependent_values) {
      return config.dependent_values;
    }
    return [];
  };

  if (!dependencies || dependencies.length === 0) {
    return (
      <Container fluid>
        {renderHeader()}

        <Alert variant="info">
          <strong>No field dependencies configured!</strong>
          <br />
          You need to create field dependencies first before you can configure their values.
        </Alert>
      </Container>
    );
  }

  // Check if the fields have picklist values
  if (!controllingField || !dependentField) {
    return (
      <Container fluid>
        {renderHeader()}

        <Alert variant="warning">
          <strong>Fields not found!</strong>
          <br />
          The controlling or dependent field could not be found. Please check your field configuration.
        </Alert>
      </Container>
    );
  }

  // Check if we have value configs loaded
  if (valueConfigs.length === 0 || isInitializing) {
    return (
      <Container fluid>
        {renderHeader()}
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading picklist values...</p>
        </div>
      </Container>
    );
  }

  const config = valueConfigs[0];

  if (!config || !config.values || config.values.length === 0 || !config.dependent_values || config.dependent_values.length === 0) {
    return (
      <Container fluid>
        {renderHeader(
          pageTitle,
          `Object: ${objectName} | ${controllingField?.field_label} → ${dependentField?.field_label}`
        )}

        <Alert variant="warning">
          <strong>No picklist values found!</strong>
          <br />
          {(!config || !config.values || config.values.length === 0) && `The controlling field "${controllingField?.field_label}" has no picklist values.`}
          {(!config || !config.dependent_values || config.dependent_values.length === 0) && `The dependent field "${dependentField?.field_label}" has no picklist values.`}
          <br />
          You need to add picklist values to these fields before configuring dependencies.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* Header */}
      {renderHeader()}

      {/* Save Button */}
      <div className="d-flex justify-content-end mb-4">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading}
        >
          <FaSave className="me-1" />
          {loading ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {/* AppToast for notifications */}
      <AppToast
        show={showToast}
        message={toastMessage}
        variant={toastVariant}
        onClose={handleToastClose}
      />

      {/* Main Content */}
      <div className="mb-5 p-4 bg-light rounded">
        <p className="text-muted mb-0">
          Select which <strong>{dependentField?.field_label || 'dependent field'}</strong> values are available for each <strong>{controllingField?.field_label || 'controlling field'}</strong> value.
        </p>
      </div>

      {/* Value Configuration Cards */}
      <Row>
        {valueConfigs.map((config) => (
          <Col key={config.dependency_id} xs={12} className="mb-4">
            <Card>
              <Card.Header className="bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">
                      <FaCog className="me-2 text-primary" />
                      {config.controlling_field_label} → {config.dependent_field_label}
                    </h6>
                    <small className="text-muted">
                      {config.controlling_field_name} → {config.dependent_field_name}
                    </small>
                  </div>
                  <Badge bg="info">
                    {config.values.length} values
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">
                    Select which {config.dependent_field_label} values are available for each {config.controlling_field_label} value:
                  </small>
                </div>

                <Row className="g-4">
                  {config.values.map((value) => {
                    const dependentField = getDependentFieldForDependency(config.dependency_id);
                    const dependentValues = getDependentValuesForField(config.dependent_field);

                    return (
                      <Col key={value.id} xs={12} sm={6} md={6} lg={4} xl={3}>
                        <div className="p-5 border rounded bg-light h-100 shadow-sm">
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="mb-0 text-primary text-truncate" title={value.value_label}>
                              <FaStar className="me-2" />
                              {value.value_label}
                            </h6>
                            <Badge bg="secondary" className="px-3 py-2">
                              {value.selected_dependent_values.length}
                            </Badge>
                          </div>

                          <Form.Group className="mb-4">
                            <Form.Label className="small mb-3 fw-semibold">
                              Available {dependentField?.field_label || 'dependent'} values:
                            </Form.Label>
                            <Form.Select
                              multiple
                              value={value.selected_dependent_values}
                              onChange={(e) => {
                                const selected = Array.from(e.target.selectedOptions, option => option.value);
                                handleDependentValueChange(config.dependency_id, value.id, selected);
                              }}
                              size="sm"
                              className="mb-4"
                            >
                              {dependentValues.map(depValue => (
                                <option key={depValue.id} value={depValue.id}>
                                  {depValue.value_label} ({depValue.value_key})
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>

                          {value.selected_dependent_values.length > 0 && (
                            <div className="mt-4">
                              <small className="text-muted d-block mb-3 fw-semibold">Selected values:</small>
                              <div className="d-flex flex-wrap gap-2">
                                {value.selected_dependent_values.map(selectedId => {
                                  const selectedValue = dependentValues.find(v => v.id === selectedId);
                                  return (
                                    <Badge
                                      key={selectedId}
                                      bg="success"
                                      className="cursor-pointer px-3 py-2"
                                      onClick={() => {
                                        const newSelected = value.selected_dependent_values.filter(v => v !== selectedId);
                                        handleDependentValueChange(config.dependency_id, value.id, newSelected);
                                      }}
                                    >
                                      {selectedValue?.value_label || selectedId}
                                      <FaTimes className="ms-1" />
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Empty State */}
      {valueConfigs.length === 0 && (
        <div className="text-center py-5">
          <div className="mb-3">
            <FaCog size={48} className="text-muted" />
          </div>
          <h5 className="text-muted">No value configurations available</h5>
          <p className="text-muted">
            Configure field dependencies first to set up value relationships.
          </p>
        </div>
      )}
    </Container>
  );
};

export default DependentValueManager;
